import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

const STATUS_TONE = {
  pending: { label: "Awaiting activation", bg: "bg-brand-teal/10 border-brand-teal/40", text: "text-brand-teal" },
  active: { label: "Active", bg: "bg-brand-success/10 border-brand-success/40", text: "text-brand-success" },
  past_due: { label: "Past due", bg: "bg-amber-50 border-amber-200", text: "text-amber-800" },
  suspended: { label: "Suspended (read-only)", bg: "bg-red-50 border-red-200", text: "text-red-800" },
  cancelled: { label: "Cancelled", bg: "bg-gray-100 border-gray-200", text: "text-gray-700" },
};

export default function PainterMembership() {
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, invRes, tiersRes] = await Promise.all([
        api.get("/membership/me"),
        api.get("/membership/invoices").catch(() => ({ data: { invoices: [] } })),
        api.get("/membership/tiers"),
      ]);
      setM(meRes.data);
      setInvoices(invRes.data?.invoices || []);
      setTiers(tiersRes.data?.tiers || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const activate = async (tier_slug, billing_cycle) => {
    setProcessing(true); setErr("");
    try {
      const { data } = await api.post("/membership/checkout", {
        tier_slug, billing_cycle,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to start checkout.");
      setProcessing(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel at the end of the current billing period? Your data stays intact — you can reactivate any time.")) return;
    setProcessing(true);
    try {
      await api.post("/membership/cancel");
      await load();
    } finally { setProcessing(false); }
  };

  const reactivate = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post("/membership/reactivate");
      if (data.needs_checkout) {
        // Full checkout again
        const active = m?.tier_slug || "basic";
        const cycle = m?.billing_cycle || "monthly";
        await activate(active, cycle);
      } else {
        await load();
      }
    } finally { setProcessing(false); }
  };

  const changeTier = async (tier_slug, billing_cycle) => {
    if (!window.confirm(`Switch to ${tier_slug.toUpperCase()} ${billing_cycle}? Any difference in price will be prorated automatically.`)) return;
    setProcessing(true); setErr("");
    try {
      await api.post("/membership/change-tier", { tier_slug, billing_cycle });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to change tier.");
    } finally { setProcessing(false); }
  };

  if (loading) return <div className="p-8 text-brand-navy" data-testid="mem-loading">Loading your membership…</div>;

  // No membership yet — show activation options.
  if (!m?.has_membership) {
    return <ActivationView tiers={tiers} activate={activate} processing={processing} err={err} />;
  }

  const status = m.status;
  const tone = STATUS_TONE[status] || STATUS_TONE.pending;
  const isActive = status === "active" || status === "past_due";
  const isSuspended = status === "suspended" || status === "cancelled";

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="painter-membership-page">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold">ColourMatch Pro</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-brand-navy">My membership</h1>
      </header>

      {err && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="mem-err">{String(err)}</div>}

      <section className={`rounded-2xl border p-5 sm:p-6 mb-6 ${tone.bg}`} data-testid="mem-status-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-brand-teal mb-1">Current status</div>
            <div className={`text-2xl font-bold ${tone.text}`} data-testid="mem-status">{tone.label}</div>
            <div className="text-sm text-gray-700 mt-1">
              <span className="font-semibold capitalize">{m.tier_slug}</span>
              {" · "}
              <span className="capitalize">{m.billing_cycle}</span>
              {m.cancel_at_period_end && <span className="ml-2 text-amber-800 font-semibold">(will cancel at period end)</span>}
            </div>
            {m.current_period_end && (
              <div className="text-xs text-gray-600 mt-1" data-testid="mem-next-renewal">
                {m.cancel_at_period_end ? "Access ends" : "Next renewal"}: {new Date(m.current_period_end).toLocaleDateString("en-AU")}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {status === "pending" && (
              <button onClick={() => activate(m.tier_slug, m.billing_cycle)} disabled={processing}
                className="px-4 py-2 rounded-lg bg-brand-teal text-white font-semibold text-sm hover:bg-brand-teal/90 disabled:opacity-50"
                data-testid="mem-activate-btn">
                {processing ? "Redirecting…" : "Activate — pay first invoice"}
              </button>
            )}
            {isActive && !m.cancel_at_period_end && (
              <button onClick={cancel} disabled={processing} className="px-4 py-2 rounded-lg border border-brand-navy text-brand-navy font-semibold text-sm hover:bg-brand-cream disabled:opacity-50" data-testid="mem-cancel-btn">
                Cancel membership
              </button>
            )}
            {(m.cancel_at_period_end || isSuspended) && (
              <button onClick={reactivate} disabled={processing} className="px-4 py-2 rounded-lg bg-brand-teal text-white font-semibold text-sm hover:bg-brand-teal/90 disabled:opacity-50" data-testid="mem-reactivate-btn">
                Reactivate membership
              </button>
            )}
          </div>
        </div>
      </section>

      {isActive && (
        <section className="mb-6" data-testid="mem-tier-switcher">
          <h2 className="text-sm font-bold uppercase tracking-widest text-brand-teal mb-3">Change your plan</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {tiers.map((t) => {
              const isCurrent = m.tier_slug === t.slug;
              return (
                <div key={t.slug} className={`border rounded-xl p-4 ${isCurrent ? "border-brand-teal bg-brand-teal/5" : "border-brand-border bg-white"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-bold text-brand-navy">{t.name}</h3>
                    {isCurrent && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-brand-teal text-white">Current</span>}
                  </div>
                  <p className="text-xs text-gray-600 mb-3">{t.positioning}</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button onClick={() => changeTier(t.slug, "monthly")} disabled={processing || (isCurrent && m.billing_cycle === "monthly")} className="flex-1 px-3 py-2 rounded-lg border border-brand-navy text-brand-navy font-semibold text-xs hover:bg-brand-cream disabled:opacity-40" data-testid={`mem-switch-${t.slug}-monthly`}>
                      Monthly {t.price_monthly_aud ? `· A$${t.price_monthly_aud}` : ""}
                    </button>
                    <button onClick={() => changeTier(t.slug, "annual")} disabled={processing || (isCurrent && m.billing_cycle === "annual")} className="flex-1 px-3 py-2 rounded-lg border border-brand-navy text-brand-navy font-semibold text-xs hover:bg-brand-cream disabled:opacity-40" data-testid={`mem-switch-${t.slug}-annual`}>
                      Annual {t.price_annual_aud ? `· A$${t.price_annual_aud}` : ""}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="mb-6" data-testid="mem-invoices">
        <h2 className="text-sm font-bold uppercase tracking-widest text-brand-teal mb-3">Invoice history</h2>
        {invoices.length === 0 ? (
          <div className="text-sm text-gray-500 italic p-3 bg-brand-cream rounded-lg" data-testid="mem-invoices-empty">No invoices yet — they&apos;ll appear here after your first payment.</div>
        ) : (
          <div className="border border-brand-border rounded-xl overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-brand-cream text-xs uppercase tracking-widest text-brand-navy">
                <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Invoice #</th><th className="text-right px-3 py-2">Amount</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-brand-border" data-testid={`mem-invoice-${inv.id}`}>
                    <td className="px-3 py-2 text-xs">{inv.created ? new Date(inv.created).toLocaleDateString("en-AU") : "—"}</td>
                    <td className="px-3 py-2 text-xs">{inv.number || inv.id.slice(-8)}</td>
                    <td className="px-3 py-2 text-right text-xs">{inv.currency} ${(inv.amount_paid || inv.amount_due).toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs capitalize">{inv.status}</td>
                    <td className="px-3 py-2 text-right">
                      {inv.hosted_invoice_url && <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-brand-teal hover:underline text-xs">View →</a>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function ActivationView({ tiers, activate, processing, err }) {
  const [selected, setSelected] = useState({ slug: "basic", cycle: "monthly" });
  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="mem-activation-view">
      <header className="mb-6 text-center">
        <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold">ColourMatch Pro</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-brand-navy mt-1">Activate your membership</h1>
        <p className="text-sm text-gray-600 mt-2">Your application has been approved. Pick a plan below to complete activation.</p>
      </header>
      {err && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{String(err)}</div>}
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        {tiers.map((t) => {
          const isSel = selected.slug === t.slug;
          const canActivate = t.stripe_price_id_monthly || t.stripe_price_id_annual;
          return (
            <button key={t.slug} onClick={() => setSelected({ ...selected, slug: t.slug })} disabled={!canActivate}
              className={`text-left border-2 rounded-2xl p-5 transition-all ${isSel && canActivate ? "border-brand-teal bg-brand-teal/5" : "border-brand-border bg-white"} ${!canActivate ? "opacity-50" : "hover:border-brand-teal/50"}`}
              data-testid={`activation-tier-${t.slug}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold text-brand-navy">{t.name}</h3>
                {isSel && canActivate && <span className="text-xs bg-brand-teal text-white px-2 py-0.5 rounded-full font-semibold">Selected</span>}
              </div>
              <p className="text-xs text-gray-600 mb-3">{t.positioning}</p>
              <div className="text-sm text-brand-navy space-y-1 mb-3">
                {t.price_monthly_aud && <div><strong>A${t.price_monthly_aud}</strong> / month</div>}
                {t.price_annual_aud && <div><strong>A${t.price_annual_aud}</strong> / year</div>}
                {!canActivate && <div className="text-xs text-red-700">Not yet configured. Please contact ColourMatch.</div>}
              </div>
              <ul className="text-xs text-gray-700 space-y-1 list-disc pl-4">
                {(t.features_confirmed || []).slice(0, 4).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </button>
          );
        })}
      </div>
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-center mb-4">
        <label className="text-sm">
          <span className="font-semibold text-brand-navy mr-2">Billing:</span>
          <select value={selected.cycle} onChange={(e) => setSelected({ ...selected, cycle: e.target.value })} className="border border-brand-border rounded px-3 py-1.5" data-testid="activation-cycle">
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
        </label>
        <button onClick={() => activate(selected.slug, selected.cycle)} disabled={processing}
          className="px-5 py-2.5 rounded-lg bg-brand-teal text-white font-semibold text-sm hover:bg-brand-teal/90 disabled:opacity-50" data-testid="activation-start-btn">
          {processing ? "Redirecting to Stripe…" : "Continue to secure checkout →"}
        </button>
      </div>
      <p className="text-xs text-center text-gray-500">Payments are securely processed by Stripe. Your ColourMatch data is never deleted — even if you cancel.</p>
    </div>
  );
}
